/**
 * B524 — the storefront's GA4 ecommerce events, for the tenant's OWN
 * Google Tag Manager container (Pathik 2026-09-23).
 *
 * Two doors, one tracker. Embedded (inline / list / dialog): our frame is a
 * different page from the tenant's, so a tag script in here would meet none
 * of their tags — each event is RELAYED over the embed protocol and the
 * loader pushes it onto the HOST page's `dataLayer`. Hosted: the page's own
 * `dataLayer`, and only when the channel names a container id.
 *
 * Walls held here: every event is parsed by `zGtmEvent` (closed set,
 * strict, no PII) before it reaches a sink — a malformed one is dropped,
 * never pushed; amounts are derived from integer minor units by string
 * arithmetic (money.ts), the float exists only in GA4's own field;
 * `purchase` fires once per order code, remembered in session storage so a
 * confirmation reload does not double-count; a sink that throws costs the
 * page nothing (telemetry never surfaces — funnel.ts's stance).
 */
import {
  dataLayerPushes,
  isGtmContainerId,
  zGtmEvent,
  type GtmContext,
  type GtmEvent,
  type GtmEventName,
  type GtmItem,
} from "@transact/contracts";
import { minorToDecimalString, type Money } from "./money.js";
import type { Distribution, Presentation } from "./context.js";

/* ------------------------------------------------------------- amounts */

export function currencyDigits(currency: string): number {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

/** GA4's `value`/`price`: a number in MAJOR units, read off the exact decimal string. */
export function gtmAmount(money: Money): number {
  return Number(minorToDecimalString(money.amount, currencyDigits(money.currency)));
}

/* --------------------------------------------------------------- items */

interface VariantLike {
  readonly variantId: string;
  readonly name: string;
  readonly displayPrice?: Money;
}
interface ProductLike {
  readonly productId: string;
  readonly name: string;
  readonly variants: readonly VariantLike[];
}
export interface LineLike {
  readonly productId: string;
  readonly productName: string;
  readonly variantId?: string;
  readonly slotId?: string;
  readonly quantity: number;
  /** What the line costs before promotions (the line total, minor units). */
  readonly amount?: Money;
}

/** A product's headline price: its lowest ticket price. */
export function productValue(p: ProductLike): Money | undefined {
  let low: Money | undefined;
  for (const v of p.variants) {
    if (v.displayPrice === undefined) continue;
    if (low === undefined || v.displayPrice.amount < low.amount) low = v.displayPrice;
  }
  return low;
}

export function productItem(p: ProductLike): GtmItem {
  const value = productValue(p);
  return { item_id: p.productId, item_name: p.name, ...(value !== undefined ? { price: gtmAmount(value) } : {}) };
}

export function listItems(products: readonly ProductLike[], listId: string): GtmItem[] {
  return products.map((p, index) => {
    const { price, ...rest } = productItem(p);
    return { ...rest, item_list_id: listId, index, ...(price !== undefined ? { price } : {}) };
  });
}

function unitOf(l: LineLike): Money | undefined {
  if (l.amount === undefined || l.quantity <= 0 || l.amount.amount % l.quantity !== 0) return undefined;
  return { amount: l.amount.amount / l.quantity, currency: l.amount.currency };
}

export function lineItems(lines: readonly LineLike[], variantNames: Readonly<Record<string, string>>): GtmItem[] {
  return lines.map((l) => {
    const unit = unitOf(l);
    const variant = l.variantId !== undefined ? variantNames[l.variantId] : undefined;
    return {
      item_id: l.productId,
      item_name: l.productName,
      ...(variant !== undefined ? { item_variant: variant } : {}),
      ...(unit !== undefined ? { price: gtmAmount(unit) } : {}),
      quantity: l.quantity,
    };
  });
}

/** The lines' pre-promotion sum, in minor units (integer addition only). */
export function linesValue(lines: readonly LineLike[], currency: string): Money {
  let amount = 0;
  for (const l of lines) if (l.amount !== undefined) amount += l.amount.amount;
  return { amount, currency };
}

const lineKey = (l: LineLike): string => `${l.productId}|${l.variantId ?? ""}|${l.slotId ?? ""}`;

/**
 * add_to_cart / remove_from_cart are the SERVER's cart compared read to
 * read — pushed after the fact, never predicted from a press. A line's
 * delta keeps its unit price.
 */
export function cartDeltas(
  prev: readonly LineLike[],
  next: readonly LineLike[],
): { readonly added: readonly LineLike[]; readonly removed: readonly LineLike[] } {
  const was = new Map<string, LineLike>();
  for (const l of prev) was.set(lineKey(l), l);
  const added: LineLike[] = [];
  const removed: LineLike[] = [];
  const delta = (l: LineLike, quantity: number): LineLike => {
    const unit = unitOf(l);
    const { amount: _amount, ...rest } = l;
    return { ...rest, quantity, ...(unit !== undefined ? { amount: { amount: unit.amount * quantity, currency: unit.currency } } : {}) };
  };
  for (const l of next) {
    const before = was.get(lineKey(l));
    was.delete(lineKey(l));
    const d = l.quantity - (before?.quantity ?? 0);
    if (d > 0) added.push(delta(l, d));
    else if (d < 0) removed.push(delta(before ?? l, -d));
  }
  for (const l of was.values()) removed.push(delta(l, l.quantity));
  return { added, removed };
}

/* ------------------------------------------------------------- context */

export function contextFor(
  ctx: { readonly distribution: Distribution; readonly presentation?: Presentation | undefined },
  channelId: string,
): GtmContext {
  if (ctx.distribution === "hosted") return { channelId, distribution: "hosted" };
  return { channelId, distribution: "embedded", presentation: ctx.presentation ?? "inline" };
}

/* --------------------------------------------------------------- sinks */

export interface GtmSink {
  push(event: GtmEvent): void;
}

/** Embedded: over the protocol to the host page's dataLayer (the loader pushes). */
export function relaySink(bridge: { sendTrack(event: GtmEvent): void }): GtmSink {
  return { push: (e) => bridge.sendTrack(e) };
}

/** Hosted: this page's own dataLayer — only for a channel that names a valid container. */
export function hostedSink(win: { dataLayer?: unknown }, containerId: string | undefined): GtmSink | undefined {
  if (!isGtmContainerId(containerId)) return undefined;
  return {
    push(e) {
      if (!Array.isArray(win.dataLayer)) win.dataLayer = [];
      (win.dataLayer as unknown[]).push(...dataLayerPushes(e));
    },
  };
}

/* ------------------------------------------------------------- tracker */

type Ecommerce<N extends GtmEventName> = Extract<GtmEvent, { event: N }>["ecommerce"];
type Tracked = Exclude<GtmEventName, "purchase" | "refund">;

export interface Tracker {
  /** Validate and push; false when the event was dropped (malformed) or no sink holds it. */
  track<N extends Tracked>(event: N, ecommerce: Ecommerce<N>): boolean;
  /** Once per `transaction_id` — across reloads while session storage holds. */
  purchase(ecommerce: Ecommerce<"purchase">): boolean;
}

const PURCHASED_KEY = "transact.gtm.purchased";

export function createTracker(options: {
  readonly sink: GtmSink | undefined;
  readonly context: GtmContext;
  readonly storage?: Pick<Storage, "getItem" | "setItem"> | null;
}): Tracker {
  const pageLife = new Set<string>();
  const remembered = (): string[] => {
    try {
      const raw = options.storage?.getItem(PURCHASED_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  };
  const emit = (candidate: unknown): boolean => {
    if (options.sink === undefined) return false;
    const parsed = zGtmEvent.safeParse(candidate);
    if (!parsed.success) return false;
    try {
      options.sink.push(parsed.data);
    } catch {
      /* telemetry never surfaces */
    }
    return true;
  };
  return {
    track(event, ecommerce) {
      return emit({ event, ecommerce, transact: options.context });
    },
    purchase(ecommerce) {
      const code = ecommerce.transaction_id;
      if (pageLife.has(code) || remembered().includes(code)) return false;
      if (!emit({ event: "purchase", ecommerce, transact: options.context })) return false;
      pageLife.add(code);
      try {
        options.storage?.setItem(PURCHASED_KEY, JSON.stringify([...remembered(), code].slice(-20)));
      } catch {
        /* memory-only: the page-life set still holds */
      }
      return true;
    },
  };
}

/* ------------------------------------------- B524 leg 2 — hosted container */

interface ScriptLike {
  id?: string;
  src?: string;
  async?: boolean;
}
interface PageLike {
  dataLayer?: unknown;
  readonly document: {
    getElementById(id: string): unknown;
    createElement(tag: "script"): ScriptLike;
    readonly head: { appendChild(el: ScriptLike): unknown };
  };
}

const GTM_SCRIPT_ID = "transact-gtm";

/**
 * GTM's documented bootstrap for the HOSTED page (RULED 2026-09-23 — never
 * the embedded frame, never admin/POS): push `gtm.start`, then append ONE
 * async `gtm.js` for the tenant's container. Idempotent (a re-render or a
 * re-mint installs nothing twice); an absent or malformed id installs
 * nothing — the id lands in a script URL on the checkout page, so the
 * shape check IS the wall. Returns whether it installed.
 */
export function installGtm(page: PageLike, containerId: string | undefined, now: () => number = Date.now): boolean {
  if (!isGtmContainerId(containerId)) return false;
  try {
    if (page.document.getElementById(GTM_SCRIPT_ID) !== null) return false;
    if (!Array.isArray(page.dataLayer)) page.dataLayer = [];
    (page.dataLayer as unknown[]).push({ "gtm.start": now(), event: "gtm.js" });
    const script = page.document.createElement("script");
    script.id = GTM_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    page.document.head.appendChild(script);
    return true;
  } catch {
    return false; // telemetry never surfaces
  }
}
